/**
 * Written notation of a piece, as extracted from its source edition (scripts/extract_notation.py):
 * staves, voices, written values, rests, ties, stems and beams, clefs, key and time signatures,
 * barlines, fermatas and grace notes. Times are in quarter notes on the same timeline as the
 * playback notes.
 */

export type Clef = 'G' | 'G8' | 'F' | 'C3' | 'C4';

export interface NPitch {
  /** Diatonic step: octave × 7 + letter index (C4 = 28). */
  step: number;
  letter: number;
  alter: number;
  octave: number;
  midi: number;
  /** Tied to the next note of the same pitch in this voice. */
  tie: boolean;
}

export interface NEvent {
  staff: number;
  voice: number;
  /** Index of the measure. */
  m: number;
  t: number;
  /** Written length in quarters (0 for a grace note). */
  dur: number;
  /** Written value: W w h q e s t x o. */
  value: string;
  dots: number;
  tuplet: [number, number] | null;
  rest: boolean;
  grace: boolean;
  slash: boolean;
  fermata: boolean;
  /** A staccato dot. */
  staccato: boolean;
  /** Stem as the edition has it: -1 up, 1 down, 0 not given. */
  stem: number;
  tupletStart: boolean;
  tupletStop: boolean;
  bracket: boolean;
  noNumber: boolean;
  measureRest: boolean;
  pitches: NPitch[];
  /** Where a rest is placed, as a step (when the edition says). */
  restStep: number | null;
  /** One letter per beam level: b begin, c continue, e end, f forward hook, k backward hook. */
  beams: string;
}

export interface NMeasure {
  bar: number;
  start: number;
  length: number;
}

export interface Notation {
  measures: NMeasure[];
  staves: number;
  clefs: Array<{ t: number; staff: number; clef: Clef }>;
  keys: Array<{ t: number; fifths: number }>;
  times: Array<{ t: number; value: string }>;
  barlines: Array<{ t: number; code: string }>;
  events: NEvent[];
}

const LETTER: Record<string, number> = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
const LETTER_PC = [0, 2, 4, 5, 7, 9, 11];
export const VALUE_Q: Record<string, number> = { W: 8, w: 4, h: 2, q: 1, e: 0.5, s: 0.25, t: 0.125, x: 0.0625, o: 0.03125 };

export function parsePitchToken(tok: string): NPitch | null {
  const m = /^([A-G])(#{1,2}|b{1,2})?(-?\d+)(~)?/.exec(tok);
  if (!m) return null;
  const letter = LETTER[m[1]];
  const alter = m[2] ? (m[2][0] === '#' ? m[2].length : -m[2].length) : 0;
  const octave = Number(m[3]);
  return { step: octave * 7 + letter, letter, alter, octave, midi: 12 * (octave + 1) + LETTER_PC[letter] + alter, tie: !!m[4] };
}

const TOKEN = /^(?:@(-?[\d.]+))?([WwhqestxoQ])(\.*)(?:\/(\d+):(\d+))?([rgGfudS[\]MBN]*)(?:=([^;]*))?(?:;([bcefk]*))?$/;

export function parseNotation(src: string): Notation {
  const lines = src.split('\n');
  const measures: NMeasure[] = lines[0]
    .split(' ')
    .filter(Boolean)
    .map((w) => {
      const [bar, start, length] = w.split(':').map(Number);
      return { bar, start, length };
    });
  const staves = Number(lines[1]) || 2;
  const clefs: Notation['clefs'] = [];
  const keys: Notation['keys'] = [];
  const times: Notation['times'] = [];
  const barlines: Notation['barlines'] = [];
  for (const w of (lines[2] ?? '').split(' ').filter(Boolean)) {
    const i = w.indexOf(':');
    const j = w.indexOf(':', i + 1);
    const t = Number(w.slice(0, i));
    const kind = w.slice(i + 1, j);
    const val = w.slice(j + 1);
    if (kind[0] === 'c') clefs.push({ t, staff: Number(kind.slice(1)), clef: val as Clef });
    else if (kind === 'k') keys.push({ t, fifths: Number(val) });
    else if (kind === 'm') times.push({ t, value: val });
    else if (kind === 'b') barlines.push({ t, code: val });
  }
  const events: NEvent[] = [];
  for (const line of lines.slice(3)) {
    if (!line) continue;
    const sp = line.indexOf(' ');
    const [staff, voice] = line.slice(0, sp).split('.').map(Number);
    const bars = line.slice(sp + 1).split('|');
    bars.forEach((barText, mi) => {
      const meas = measures[mi];
      if (!meas) return;
      let cursor = 0;
      for (const tok of barText.split(' ')) {
        if (!tok) continue;
        const m = TOKEN.exec(tok);
        if (!m) continue;
        if (m[1] !== undefined) cursor = Number(m[1]);
        const value = m[2];
        const dots = m[3].length;
        const tuplet: [number, number] | null = m[4] ? [Number(m[4]), Number(m[5])] : null;
        const flags = m[6] ?? '';
        const grace = flags.includes('g') || flags.includes('G');
        const rest = flags.includes('r');
        const measureRest = flags.includes('M');
        let dur = VALUE_Q[value] * (2 - 0.5 ** dots);
        if (tuplet) dur = (dur * tuplet[1]) / tuplet[0];
        if (measureRest) dur = meas.length - cursor;
        const pitchToks = m[7] ? m[7].split(',') : [];
        const pitches: NPitch[] = [];
        let restStep: number | null = null;
        for (const p of pitchToks) {
          const pp = parsePitchToken(p);
          if (!pp) continue;
          if (rest) restStep = pp.step;
          else pitches.push(pp);
        }
        events.push({
          staff,
          voice,
          m: mi,
          t: Math.round((meas.start + cursor) * 1e6) / 1e6,
          dur: grace ? 0 : dur,
          value,
          dots,
          tuplet,
          rest,
          grace,
          slash: flags.includes('G'),
          fermata: flags.includes('f'),
          staccato: flags.includes('S'),
          stem: flags.includes('u') ? -1 : flags.includes('d') ? 1 : 0,
          tupletStart: flags.includes('['),
          tupletStop: flags.includes(']'),
          bracket: flags.includes('B'),
          noNumber: flags.includes('N'),
          measureRest,
          pitches,
          restStep,
          beams: m[8] ?? '',
        });
        if (!grace) cursor += dur;
      }
    });
  }
  events.sort((a, b) => a.t - b.t || a.staff - b.staff || a.voice - b.voice || Number(b.grace) - Number(a.grace));
  // A bar split in two by the edition (a chorale line ending mid-bar) is one bar, as in the playback.
  const merged: NMeasure[] = [];
  const index: number[] = [];
  for (const m of measures) {
    const prev = merged[merged.length - 1];
    if (prev && prev.bar === m.bar && Math.abs(prev.start + prev.length - m.start) < 1e-6) prev.length += m.length;
    else merged.push({ ...m });
    index.push(merged.length - 1);
  }
  for (const e of events) e.m = index[e.m] ?? e.m;
  const ends = new Set(merged.map((m) => Math.round((m.start + m.length) * 1e4)));
  const starts = new Set(merged.map((m) => Math.round(m.start * 1e4)));
  const kept = barlines.filter((b) => ends.has(Math.round(b.t * 1e4)) || starts.has(Math.round(b.t * 1e4)));
  return { measures: merged, staves, clefs, keys, times, barlines: kept, events };
}

/**
 * The sounding notes the notation implies (ties joined, grace notes left out), as [onset, midi]
 * pairs: what a check compares with the playback notes of the piece.
 */
export function soundingOf(n: Notation): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  const open = new Map<string, [number, number, number]>();
  const byStream = new Map<string, NEvent[]>();
  for (const e of n.events) {
    if (e.grace || e.rest) continue;
    const k = `${e.staff}.${e.voice}`;
    const list = byStream.get(k) ?? [];
    list.push(e);
    byStream.set(k, list);
  }
  for (const [k, list] of byStream) {
    for (const e of list) {
      for (const p of e.pitches) {
        const key = `${k}:${p.midi}`;
        const held = open.get(key);
        if (held && Math.abs(held[0] + held[2] - e.t) < 1e-3) {
          held[2] += e.dur;
        } else {
          const note: [number, number, number] = [e.t, p.midi, e.dur];
          out.push(note);
          if (p.tie) open.set(key, note);
          continue;
        }
        if (!p.tie) open.delete(key);
      }
    }
  }
  return out.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
}
